import React, { useEffect, useState } from "react";
import FolderTree from "../tasks/FolderTree";
import FolderContent from "../tasks/FolderContent";
import TaskItemExample from "../tasks/TaskItemExample";
import ShareTask from "../tasks/ShareTask";
import { Task, TaskExample, TaskTag, TaskTagTask, proficiencyLevelsOfTask, aimsOfTask, TaskSection, emptyTaskSection } from "../interfaces/Task";
import { useNavigate } from "react-router-dom";
import { Accordion, Tab, Tabs, Button, Modal, Form } from "react-bootstrap";
import TaskItemShared from "../tasks/TaskItemShared";
import { useTranslation } from "react-i18next";
import { getSharedTasksConcrete, getSharedTasksGroups, getTaskExamples, getUserTasks } from "../../services/tasks";
import { handleApiError, safeJsonParse } from "../../utils/api";
import { LoadingSpinner } from "../elements/LoadingSpinner";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

export default function MyRecentTasks() {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const [tabKey, setTabKey] = useState('my-tasks');

    const userId = localStorage.getItem('userId') ?? '';

    const [tasks, setTasks] = useState<Task[]>([]);
    const [selectedTag, setSelectedTag] = useState('');
    const [selectedProficiencyLevel, setSelectedProficiencyLevel] = useState('');
    const [selectedAim, setSelectedAim] = useState('');
    const [selectedTagExamples, setSelectedTagExamples] = useState('');
    const [selectedProficiencyLevelExamples, setSelectedProficiencyLevelExamples] = useState('');
    const [selectedAimExamples, setSelectedAimExamples] = useState('');
    const [taskTagTasksExamples, setTasksTagTasksExamples] = useState<TaskTagTask[]>([]);
    const [taskTagTasks, setTasksTagTasks] = useState<TaskTagTask[]>([]);
    const [sections, setSections] = useState<TaskSection[]>([]);
    const [sectionsLoaded, setSectionsLoaded] = useState(false);
    const [showSectionModal, setShowSectionModal] = useState(false);
    const [editingSection, setEditingSection] = useState<TaskSection | null>(null);
    const [sectionName, setSectionName] = useState('');
    const [parentSectionId, setParentSectionId] = useState<string | null>(null);
    const [tasksLoaded, setTasksLoaded] = useState(false);
    const [taskId, setTaskId] = useState('');
    const [showExamples, setShowExamples] = useState(false);
    const [examples, setExamples] = useState<TaskExample[]>([]);
    const [examplesLoaded, setExamplesLoaded] = useState(false);
    const [sharedConcrete, setSharedConcrete] = useState<any[]>([]);
    const [sharedConcreteLoaded, setSharedConcreteLoaded] = useState(false);
    const [sharedGroups, setSharedGroups] = useState<any[]>([]);
    const [sharedGroupsLoaded, setSharedGroupsLoaded] = useState(false);
    const [tags, setTags] = useState<TaskTag[]>([]);
    const [tagsLoaded, setTagsLoaded] = useState(false);
    const [taskTags, setTaskTags] = useState<TaskTagTask[]>([]);
    const [taskTagsLoaded, setTaskTagsLoaded] = useState(false);

    // Share dialog state
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [taskToShare, setTaskToShare] = useState<Task | null>(null);

    // File explorer state
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(() => {
        return sessionStorage.getItem('taskgen_selected_folder') || null;
    });
    const [expandedFolders, setExpandedFolders] = useState<string[]>(() => {
        const saved = sessionStorage.getItem('taskgen_expanded_folders');
        return saved ? JSON.parse(saved) : [];
    });
    const [viewMode, setViewMode] = useState<'icons' | 'list'>(() => {
        return (sessionStorage.getItem('taskgen_view_mode') as 'icons' | 'list') || 'icons';
    });

    // Persist selected folder
    useEffect(() => {
        if (selectedFolderId) {
            sessionStorage.setItem('taskgen_selected_folder', selectedFolderId);
        } else {
            sessionStorage.removeItem('taskgen_selected_folder');
        }
    }, [selectedFolderId]);

    // Persist expanded folders
    useEffect(() => {
        sessionStorage.setItem('taskgen_expanded_folders', JSON.stringify(expandedFolders));
    }, [expandedFolders]);

    // Persist view mode
    useEffect(() => {
        sessionStorage.setItem('taskgen_view_mode', viewMode);
    }, [viewMode]);

    // Multi-selection state
    const [selectedItems, setSelectedItems] = useState<{ id: string; type: 'section' | 'task' }[]>([]);

    // Handle item selection (with Ctrl for multi-select)
    const handleItemSelect = (id: string, type: 'section' | 'task', ctrlKey: boolean) => {
        setSelectedItems(prev => {
            const isSelected = prev.some(item => item.id === id && item.type === type);
            if (ctrlKey) {
                // Toggle selection
                if (isSelected) {
                    return prev.filter(item => !(item.id === id && item.type === type));
                } else {
                    return [...prev, { id, type }];
                }
            } else {
                // Single selection
                if (isSelected && prev.length === 1) {
                    return []; // Deselect if clicking on the only selected item
                }
                return [{ id, type }];
            }
        });
    };

    // Clear selection
    const clearSelection = () => {
        setSelectedItems([]);
    };

    // Check if item is selected
    const isItemSelected = (id: string, type: 'section' | 'task') => {
        return selectedItems.some(item => item.id === id && item.type === type);
    };

    // Delete selected items
    const handleDeleteSelected = () => {
        if (selectedItems.length === 0) return;

        const taskCount = selectedItems.filter(i => i.type === 'task').length;
        const folderCount = selectedItems.filter(i => i.type === 'section').length;

        let message = t("Are you sure you want to delete");
        if (taskCount > 0 && folderCount > 0) {
            message += ` ${taskCount} ${t("tasks")} ${t("and")} ${folderCount} ${t("folders")}?`;
        } else if (taskCount > 0) {
            message += ` ${taskCount} ${t("tasks")}?`;
        } else {
            message += ` ${folderCount} ${t("folders")}?`;
        }

        if (window.confirm(message)) {
            // Delete tasks
            selectedItems.filter(i => i.type === 'task').forEach(item => {
                handleDeleteTask(item.id);
            });
            // Delete folders
            selectedItems.filter(i => i.type === 'section').forEach(item => {
                handleDeleteSection(item.id);
            });
            clearSelection();
        }
    };

    // Move selected items to folder
    const handleMoveSelectedToFolder = (targetFolderId: string | null) => {
        selectedItems.forEach(item => {
            handleDropIntoFolder(item.id, item.type, targetFolderId);
        });
        clearSelection();
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                clearSelection();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleNewTask = (sectionId?: string | null) => {
        // Create a new task
        const requestOptionsPost = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify({
                name: 'New task',
                pretasks: [],
                tasks: [],
                postasks: [],
                updated: new Date(),
                userId: userId,
                active: false,
                sectionId: sectionId || undefined
            })
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task', requestOptionsPost)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                const taskId =  data._id;
                setTaskId(taskId);

                // Decide if user has to set level or not
                const requestOptions = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + localStorage.getItem('token')
                    }
                };
                fetch(import.meta.env.VITE_API_URL + '/v1/user/' + userId, requestOptions)
                    .then(response => {
                        if (handleApiError(response, navigate)) {
                            return null;
                        }
                        return safeJsonParse(response);
                    })
                    .then(data => {
                        if (!data) return;
                        navigate('/edit-task/' + taskId);
                    });
            });
    }

    const handleDeleteTask = (id: string) => {
        if (window.confirm(t("Are you sure you want to delete this task?"))) {
            const requestOptions = {
                method: 'DELETE',
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task/' + id, requestOptions)
                .then(() => {
                    // Use functional form to handle multiple concurrent deletes
                    setTasks(prevTasks => prevTasks.filter((t: Task) => t._id != id));
                });
        }
    }

    const handleDuplicateTask = (task: Task) => {
        const newTaskData = {
            ...task,
            _id: undefined,
            name: task.name + ' (copy)',
            userId: userId
        };

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(newTaskData)
        };

        fetch(import.meta.env.VITE_API_URL + '/v1/task', requestOptions)
            .then(response => safeJsonParse(response))
            .then(data => {
                if (data) {
                    setTasks(prevTasks => [data, ...prevTasks]);
                }
            });
    }

    const handleDuplicateTaskExample = (task: Task) => {
        const newTask = Object.assign({}, task);
        setTasks(prevTasks => [newTask, ...prevTasks]);
        setShowExamples(false);
        alert(t("The task has been cloned. You will find the cloned task in \"My Task designs\" in the dashboard"))
    }

    const handleDuplicateTaskShared = (task: Task) => {
        const newTask = Object.assign({}, task);
        setTasks(prevTasks => [newTask, ...prevTasks]);
        setShowExamples(false);
        alert(t("The task has been cloned. You will find the cloned task in \"My Task designs\" in the dashboard"))
    }

    const handleShowExamples = () => {
        setTabKey('example-tasks');
    }

    useEffect(() => {
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };

        // Load tags
        fetch(import.meta.env.VITE_API_URL + '/v1/task-tags/' + userId, requestOptions)
            .then(response => {
                return safeJsonParse(response);
            })
            .then(data => {
                setTags(data);
                setTagsLoaded(true);
            });

        // Load sections
        fetch(import.meta.env.VITE_API_URL + '/v1/task-sections/' + userId, requestOptions)
            .then(response => {
                return safeJsonParse(response);
            })
            .then(data => {
                setSections(data || []);
                setSectionsLoaded(true);
            })
            .catch(() => {
                setSections([]);
                setSectionsLoaded(true);
            });

        getUserTasks(userId)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (data) {
                    // Filter to only include tasks owned by the current user
                    // The backend may return shared tasks too
                    const ownTasks = data.filter((t: Task) => {
                        const taskUserId = typeof t.userId === 'object' ? (t.userId as any)._id : t.userId;
                        return taskUserId === userId;
                    });
                    setTasks(ownTasks);
                    setTasksLoaded(true);
                }
            });

        // Load example tasks
        getTaskExamples()
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (data) {
                    setExamples(data);
                    setExamplesLoaded(true);
                }
            });

        // Load shared tasks
        getSharedTasksConcrete(userId)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                const newSharedConcrete: any[] = [];
                if (data.length > 0) {
                    // Debug: check what backend returns
                    console.log('Shared task data:', data[0]);
                    console.log('task.userId:', data[0]?.task?.userId);

                    data.sort((a, b) => a.updated > b.updated ? 1 : -1).map((d: any) => {
                        if (d.task != null) {
                            if (newSharedConcrete.findIndex((i: any) => i.id == d.task._id) === -1) {
                                // Get owner's full name (the person who owns/shared the task)
                                let userFullName = 'Unknown';
                                if (d.task.userId && typeof d.task.userId === 'object' && d.task.userId.profile) {
                                    userFullName = [d.task.userId.profile.firstname, d.task.userId.profile.lastname].filter(Boolean).join(' ') || 'Unknown';
                                }

                                newSharedConcrete.push({
                                    _id: d.task._id,
                                    name: d.task.name,
                                    updated: d.task.updated,
                                    userId: typeof d.task.userId === 'object' ? d.task.userId._id : d.task.userId,
                                    userFullName: userFullName,
                                    permission: d.permission || 'view',
                                    canEdit: d.permission === 'edit'
                                });
                            }
                        }
                    });
                }
                setSharedConcrete(newSharedConcrete);
                setSharedConcreteLoaded(true);
            });

        getSharedTasksGroups(userId)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                const newSharedGroups: any[] = [];
                if (data.length > 0) {
                    data.sort((a, b) => {
                        // Handle null tasks in sorting
                        if (!a.task || !b.task) return 0;
                        return a.task.updated > b.task.updated ? 1 : -1;
                    }).map((d: any) => {
                        if (d.task != null) {
                            if (newSharedGroups.findIndex((i: any) => i.id == d.task._id) === -1) {
                                // Safely get group name
                                const groupName = d.group && d.group.name ? d.group.name : 'Unknown';
                                // Use userPermission from backend response
                                const permission = d.userPermission || 'view';
                                newSharedGroups.push({
                                    _id: d.task._id,
                                    name: d.task.name,
                                    updated: d.task.updated,
                                    group: groupName,
                                    permission: permission,
                                    canEdit: permission === 'edit'
                                });
                            }
                        }
                    });
                }
                setSharedGroups(newSharedGroups);
                setSharedGroupsLoaded(true);
            });
    }, []);


    const handleFilterTag = (event: any) => {
        const tagId = event.currentTarget.value;
        setSelectedTag(tagId);
        if (tagId != "") {
            const requestOptions = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-tags/tag/' + tagId, requestOptions)
                .then(response => {
                    return safeJsonParse(response);
                })
                .then(data => {
                    setTasksTagTasks(data);
                });
        } else {
            setTasksTagTasks([]);
        }
    }

    const handleFilterProficiencyLevel = (event: any) => {
        const levelValue = event.currentTarget.value;
        setSelectedProficiencyLevel(levelValue);
    }

    const handleFilterAim = (event: any) => {
        const aimValue = event.currentTarget.value;
        setSelectedAim(aimValue);
    }

    const handleFilterTagExamples = (event: any) => {
        const tagId = event.currentTarget.value;
        setSelectedTagExamples(tagId);
        if (tagId != "") {
            const requestOptions = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-tags/tag/' + tagId, requestOptions)
                .then(response => {
                    return safeJsonParse(response);
                })
                .then(data => {
                    setTasksTagTasksExamples(data);
                });
        } else {
            setTasksTagTasksExamples([]);
        }
    }

    const handleFilterProficiencyLevelExamples = (event: any) => {
        const levelValue = event.currentTarget.value;
        setSelectedProficiencyLevelExamples(levelValue);
    }

    const handleFilterAimExamples = (event: any) => {
        const aimValue = event.currentTarget.value;
        setSelectedAimExamples(aimValue);
    }

    // Helper function to reload sections from API
    const reloadSections = () => {
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task-sections/' + userId, requestOptions)
            .then(response => safeJsonParse(response))
            .then(data => {
                setSections(data || []);
            })
            .catch(() => {
                setSections([]);
            });
    };

    // Helper function to flatten sections tree for dropdown
    const flattenSections = (sections: TaskSection[], excludeId?: string, level = 0): { section: TaskSection; level: number }[] => {
        let result: { section: TaskSection; level: number }[] = [];
        for (const section of sections) {
            if (section._id !== excludeId) {
                result.push({ section, level });
                if (section.children && section.children.length > 0) {
                    result = result.concat(flattenSections(section.children, excludeId, level + 1));
                }
            }
        }
        return result;
    };

    // Helper function to check if a section is descendant of another
    const isDescendantOf = (sections: TaskSection[], parentId: string, childId: string): boolean => {
        const findInChildren = (children: TaskSection[]): boolean => {
            for (const child of children) {
                if (child._id === childId) return true;
                if (child.children && findInChildren(child.children)) return true;
            }
            return false;
        };

        const parent = findSectionInTree(sections, parentId);
        if (!parent || !parent.children) return false;
        return findInChildren(parent.children);
    };

    // Helper function to find a section in the tree
    const findSectionInTree = (sections: TaskSection[], id: string): TaskSection | undefined => {
        for (const section of sections) {
            if (section._id === id) return section;
            if (section.children) {
                const found = findSectionInTree(section.children, id);
                if (found) return found;
            }
        }
        return undefined;
    };

    // Get folder path for breadcrumb
    const getFolderPath = (folderId: string | null): TaskSection[] => {
        if (folderId === null) return [];
        const path: TaskSection[] = [];
        let current = findSectionInTree(sections, folderId);
        while (current) {
            path.unshift(current);
            current = current.parentId ? findSectionInTree(sections, current.parentId) : undefined;
        }
        return path;
    };

    // Get subfolders of current folder
    const getSubfolders = (folderId: string | null): TaskSection[] => {
        if (folderId === null) {
            // Root level folders
            return sections.filter(s => !s.parentId);
        }
        const folder = findSectionInTree(sections, folderId);
        return folder?.children || [];
    };

    // Check if any filter is active
    const hasActiveFilters = selectedTag !== '' || selectedProficiencyLevel !== '' || selectedAim !== '';

    // Get tasks in current folder with filters applied
    // When filtering, search across ALL tasks (transversal search)
    const getFolderTasks = (folderId: string | null): Task[] => {
        // If filters are active, search all tasks regardless of folder
        const folderTasks = hasActiveFilters
            ? tasks
            : (folderId === null
                ? tasks.filter(t => !t.sectionId)
                : tasks.filter(t => t.sectionId === folderId));

        return folderTasks.filter(t => {
            const tagIndex = taskTagTasks.findIndex((tt: TaskTagTask) => tt.taskId == t._id);
            const tagMatches = selectedTag == '' || (selectedTag != '' && taskTagTasks.length > 0 && tagIndex !== -1);
            const proficiencyMatches = selectedProficiencyLevel == '' || (selectedProficiencyLevel != '' && t.proficiency_level && t.proficiency_level.includes(parseInt(selectedProficiencyLevel)));
            const aimMatches = selectedAim == '' || (selectedAim != '' && t.aims && t.aims.includes(parseInt(selectedAim)));
            return tagMatches && proficiencyMatches && aimMatches;
        });
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSelectedTag('');
        setSelectedProficiencyLevel('');
        setSelectedAim('');
    };

    const handleToggleExpand = (folderId: string) => {
        setExpandedFolders(prev => {
            if (prev.includes(folderId)) {
                return prev.filter(id => id !== folderId);
            } else {
                return [...prev, folderId];
            }
        });
    };

    const handleCreateSection = (parentId: string | null = null) => {
        setEditingSection(null);
        setSectionName('');
        setParentSectionId(parentId);
        setShowSectionModal(true);
    }

    const handleEditSection = (section: TaskSection) => {
        setEditingSection(section);
        setSectionName(section.name);
        setParentSectionId(section.parentId);
        setShowSectionModal(true);
    }

    const handleSaveSection = () => {
        if (!sectionName.trim()) return;

        if (editingSection) {
            // Update existing section
            const requestOptions = {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                },
                body: JSON.stringify({
                    ...editingSection,
                    name: sectionName.trim(),
                    parentId: parentSectionId
                })
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-sections/' + editingSection._id, requestOptions)
                .then(response => safeJsonParse(response))
                .then(() => {
                    // Reload sections to get updated tree structure
                    reloadSections();
                    setShowSectionModal(false);
                });
        } else {
            // Create new section
            const newSection = {
                name: sectionName.trim(),
                userId: userId,
                parentId: parentSectionId,
                expanded: true
            };
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                },
                body: JSON.stringify(newSection)
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-sections', requestOptions)
                .then(response => safeJsonParse(response))
                .then(() => {
                    // Reload sections to get updated tree structure
                    reloadSections();
                    setShowSectionModal(false);
                });
        }
    }

    const handleDeleteSection = (sectionId: string) => {
        if (window.confirm(t("Are you sure you want to delete this section? Subsections will be promoted and tasks will be moved to 'Unsectioned'."))) {
            const requestOptions = {
                method: 'DELETE',
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-sections/' + sectionId, requestOptions)
                .then(() => {
                    // If we're currently viewing the deleted section, go to root
                    if (selectedFolderId === sectionId) {
                        setSelectedFolderId(null);
                    }
                    // Reload sections to get updated tree (children are promoted by API)
                    reloadSections();
                    // Move tasks from deleted section to unsectioned
                    setTasks(prevTasks => prevTasks.map(t => t.sectionId === sectionId ? {...t, sectionId: undefined} : t));
                });
        }
    }

    // Handle dropping items into folders
    const handleDropIntoFolder = (itemId: string, itemType: 'section' | 'task', targetFolderId: string | null) => {
        if (itemType === 'section') {
            handleDropSectionIntoFolder(itemId, targetFolderId);
        } else {
            handleDropTaskIntoFolder(itemId, targetFolderId);
        }
    };

    const handleDropSectionIntoFolder = (sectionId: string, targetFolderId: string | null) => {
        const draggedSection = findSectionInTree(sections, sectionId);
        if (!draggedSection) return;

        // Prevent dropping a section into itself
        if (sectionId === targetFolderId) return;

        // Prevent dropping a section into its own descendant
        if (targetFolderId && isDescendantOf(sections, sectionId, targetFolderId)) {
            console.error('Cannot move a section into its own descendant');
            return;
        }

        // Update the section's parentId
        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify({
                ...draggedSection,
                parentId: targetFolderId
            })
        };

        fetch(import.meta.env.VITE_API_URL + '/v1/task-sections/' + sectionId, requestOptions)
            .then(response => safeJsonParse(response))
            .then(() => {
                // Reload sections to get the updated tree structure
                reloadSections();
            })
            .catch(error => {
                console.error('Error moving section:', error);
            });
    };

    const handleDropTaskIntoFolder = (taskId: string, targetFolderId: string | null) => {
        const task = tasks.find(t => t._id === taskId);
        if (!task) return;

        // Update task's sectionId
        const taskUpdate = {
            ...task,
            sectionId: targetFolderId || undefined
        };

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(taskUpdate)
        };

        fetch(import.meta.env.VITE_API_URL + '/v1/task/' + taskId, requestOptions)
            .then(response => safeJsonParse(response))
            .then(data => {
                // Update local state using functional form to handle multiple concurrent updates
                setTasks(prevTasks => prevTasks.map(t => t._id === taskId ? data : t));
            })
            .catch(error => {
                console.error('Error moving task:', error);
            });
    };

    const handleUnshareTask = (taskId: string) => {
        const newSharedConcrete = sharedConcrete.filter((t: Task) => t._id == taskId);
        setSharedConcrete(newSharedConcrete);
    }

    const handleEditTask = (taskId: string) => {
        navigate('/edit-task/' + taskId);
    };

    const handleShareTask = (task: Task) => {
        setTaskToShare(task);
        setShowShareDialog(true);
    };

    const handleShareLinkTask = (task: Task) => {
        navigator.clipboard.writeText(
            window.location.protocol + '//' +
            window.location.hostname +
            (window.location.port ? ':' + window.location.port : '') +
            '/#/do-share/' + task._id
        ).then(() => {
            alert(t("Sharing link copied to your clipboard."));
        });
    };

    const handleShareClose = () => {
        setShowShareDialog(false);
        setTaskToShare(null);
    };

    const handleShareConfirm = () => {
        setShowShareDialog(false);
        setTaskToShare(null);
    };

    // Get current folder object
    const currentFolder = selectedFolderId ? findSectionInTree(sections, selectedFolderId) || null : null;

    return(
        <div className="my-recent-tasks box">
            <Tabs id="task-tabs" defaultActiveKey={"my-tasks"} activeKey={ tabKey } onSelect={(k) => setTabKey(k ?? 'my-tasks')}>
                <Tab eventKey="my-tasks" title={ t("My task designs") }>
                    { !tasksLoaded && <LoadingSpinner /> }
                    { tasksLoaded && tasks.length == 0 && sections.length === 0 && !showExamples &&
                        <div className="no-tasks">
                            <div className="margin-bottom-small">
                                <button className="btn btn-primary btn-lg" onClick={ () => handleNewTask() }>
                                    { t("Wish to design a task?") }
                                </button>
                            </div>
                            <div className="margin-bottom-small">
                                { t("or get some inspiration") }
                            </div>
                            <button className="btn btn-primary" onClick={ handleShowExamples }>
                                { t("See some examples") }
                            </button>
                        </div>
                    }
                    { tasksLoaded && (tasks.length > 0 || sections.length > 0) && !showExamples && sectionsLoaded &&
                        <DndProvider backend={HTML5Backend}>
                            <div className="file-explorer">
                                <FolderTree
                                    sections={sections}
                                    selectedFolderId={selectedFolderId}
                                    expandedFolders={expandedFolders}
                                    onSelectFolder={setSelectedFolderId}
                                    onToggleExpand={handleToggleExpand}
                                    onDropIntoFolder={handleDropIntoFolder}
                                    onEditFolder={handleEditSection}
                                    onDeleteFolder={handleDeleteSection}
                                    onCreateFolder={handleCreateSection}
                                    onCreateTask={handleNewTask}
                                />
                                <FolderContent
                                    currentFolder={currentFolder}
                                    folderPath={getFolderPath(selectedFolderId)}
                                    subfolders={getSubfolders(selectedFolderId)}
                                    tasks={getFolderTasks(selectedFolderId)}
                                    viewMode={viewMode}
                                    onViewModeChange={setViewMode}
                                    onNavigateToFolder={setSelectedFolderId}
                                    onEditTask={handleEditTask}
                                    onDeleteTask={handleDeleteTask}
                                    onDuplicateTask={handleDuplicateTask}
                                    onShareTask={handleShareTask}
                                    onShareLinkTask={handleShareLinkTask}
                                    onCreateFolder={() => handleCreateSection(selectedFolderId)}
                                    onCreateSubfolder={handleCreateSection}
                                    onCreateTask={() => handleNewTask(selectedFolderId)}
                                    onEditFolder={handleEditSection}
                                    onDeleteFolder={handleDeleteSection}
                                    onDropItem={handleDropIntoFolder}
                                    selectedTag={selectedTag}
                                    onTagChange={handleFilterTag}
                                    selectedProficiencyLevel={selectedProficiencyLevel}
                                    onProficiencyLevelChange={handleFilterProficiencyLevel}
                                    selectedAim={selectedAim}
                                    onAimChange={handleFilterAim}
                                    onClearFilters={handleClearFilters}
                                    tags={tags}
                                    proficiencyLevels={proficiencyLevelsOfTask}
                                    aims={aimsOfTask}
                                    selectedItems={selectedItems}
                                    onItemSelect={handleItemSelect}
                                    onClearSelection={clearSelection}
                                    onDeleteSelected={handleDeleteSelected}
                                    isItemSelected={isItemSelected}
                                />
                            </div>
                        </DndProvider>
                    }
                </Tab>
                <Tab eventKey="shared-tasks" title={ t("Shared with me") }>
                    <div className="shared-container">
                        <Accordion defaultActiveKey={['0', '1']} alwaysOpen>
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>{ t("Shared directly with me") }</Accordion.Header>
                                <Accordion.Body>
                                    { !sharedConcreteLoaded && <LoadingSpinner /> }
                                    { sharedConcreteLoaded &&
                                        sharedConcrete.map((e: Task) => {
                                            return <TaskItemShared task={ e } key={ 'task-shared-' + e._id }
                                                duplicateCallback={ handleDuplicateTaskExample }
                                                deleteCallback={ handleUnshareTask }
                                                 />;
                                        })
                                    }
                                </Accordion.Body>
                            </Accordion.Item>
                            <Accordion.Item eventKey="1">
                                <Accordion.Header>{ t("Shared by group") }</Accordion.Header>
                                <Accordion.Body>
                                    { !sharedGroupsLoaded && <LoadingSpinner /> }
                                    { sharedGroupsLoaded &&
                                        sharedGroups.map((e: Task) => {
                                            return <TaskItemShared task={ e } duplicateCallback={ handleDuplicateTaskExample } deleteCallback={ handleUnshareTask }
                                                key={ 'task-shared-' + e._id } />;
                                        })
                                    }
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </div>
                </Tab>
                <Tab eventKey="example-tasks" title={ t("Task examples") }>
                    <div className="examples-container">
                        { examplesLoaded && examples.length > 0 &&
                            <div className="has-tasks">
                                <div className="actions">
                                    { tagsLoaded &&
                                        <div className="actio-filters">
                                            <select name="selected_proficiency_level_examples" className="proficiency-filter" onChange={ handleFilterProficiencyLevelExamples }>
                                                <option value="">{ t("- Select a proficiency level -") }</option>
                                                {
                                                    proficiencyLevelsOfTask.map((level) => {
                                                        return <option key={ 'level-examples-' + level.value} value={ level.value}>
                                                            { level.description }
                                                        </option>
                                                    })
                                                }
                                            </select>
                                            <select name="selected_aim_examples" className="aim-filter" onChange={ handleFilterAimExamples }>
                                                <option value="">{ t("- Select an aim -") }</option>
                                                {
                                                    aimsOfTask.map((aim) => {
                                                        return <option key={ 'aim-examples-' + aim.value} value={ aim.value}>
                                                            { aim.description }
                                                        </option>
                                                    })
                                                }
                                            </select>
                                        </div>
                                    }
                                </div>
                            </div>
                        }
                        { !examplesLoaded && <LoadingSpinner /> }
                        { examplesLoaded &&
                            examples.filter((e: TaskExample) => {
                                // Filter out examples with null tasks
                                if (!e.task || !e.task._id) {
                                    return false;
                                }
                                const tagIndex = taskTagTasksExamples.findIndex((tt: TaskTagTask) => tt.taskId == e.task._id);
                                const tagMatches = selectedTagExamples == '' || (selectedTagExamples != '' && taskTagTasksExamples.length > 0 && tagIndex !== -1);
                                const proficiencyMatches = selectedProficiencyLevelExamples == '' || (selectedProficiencyLevelExamples != '' && (e.task as any).proficiency_level && (e.task as any).proficiency_level.includes(parseInt(selectedProficiencyLevelExamples)));
                                const aimMatches = selectedAimExamples == '' || (selectedAimExamples != '' && (e.task as any).aims && (e.task as any).aims.includes(parseInt(selectedAimExamples)));
                                return tagMatches && proficiencyMatches && aimMatches;
                            }).map((e: TaskExample) => {
                                return <TaskItemExample task={ e } duplicateCallback={ handleDuplicateTaskShared }
                                    key={ 'task-example-' + e._id } />;
                            })
                        }
                    </div>
                </Tab>
            </Tabs>

            {/* Section Modal */}
            <Modal show={showSectionModal} onHide={() => setShowSectionModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingSection ? t("Edit Section") : t("Create Section")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={(e) => {
                        e.preventDefault();
                        if (sectionName.trim()) {
                            handleSaveSection();
                        }
                    }}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("Section Name")}</Form.Label>
                            <Form.Control
                                type="text"
                                value={sectionName}
                                onChange={(e) => setSectionName(e.target.value)}
                                placeholder={t("Enter section name")}
                                autoFocus
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t("Parent Section")}</Form.Label>
                            <Form.Select
                                value={parentSectionId || ''}
                                onChange={(e) => setParentSectionId(e.target.value || null)}
                            >
                                <option value="">{t("None (root level)")}</option>
                                {flattenSections(sections, editingSection?._id).map(({ section, level }) => (
                                    <option key={section._id} value={section._id}>
                                        {'—'.repeat(level)} {section.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                {t("Select a parent section to create a subsection")}
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSectionModal(false)}>
                        {t("Cancel")}
                    </Button>
                    <Button variant="primary" onClick={handleSaveSection} disabled={!sectionName.trim()}>
                        {editingSection ? t("Update") : t("Create")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Share Task Dialog */}
            {showShareDialog && taskToShare && (
                <ShareTask
                    task={taskToShare}
                    shareCallback={handleShareConfirm}
                    closeCallback={handleShareClose}
                />
            )}
        </div>);
}
